import { Component } from "@theme/component";
import { CartActions } from "@theme/cart-actions";
//@ts-ignore
import BlazeSlider from "@lib/blaze-slider";

/** @returns {HTMLElement | null} */
function getMysteryOfferDialog() {
  return document.querySelector(".cart-drawer-addon__mystery dialog-component");
}

/** Mystery markup exists (offer still available for this cart). */
function mysteryOfferPresent() {
  const mysteryDialog = getMysteryOfferDialog();
  return Boolean(mysteryDialog?.querySelector("dialog"));
}

/**
 * Modal is open — user must choose before checkout may proceed programmatically.
 * Closed + submit means Decline/Claim already ran; do not re-gate.
 */
function mysteryCheckoutModalIsOpen() {
  const inner = getMysteryOfferDialog()?.querySelector("dialog");
  return inner instanceof HTMLDialogElement && inner.open;
}

/**
 * Live cart check so we do not show the offer after any paid Mystery Knife
 * (settings product / main swap / any swap variant) is already in the cart.
 *
 * @param {number} variantId
 * @param {number[]} [blockVariantIds]
 * @param {number[]} [blockProductIds]
 * @returns {Promise<boolean>}
 */
async function mysteryAlreadyInCart(
  variantId,
  blockVariantIds = [],
  blockProductIds = [],
) {
  try {
    const response = await fetch("/cart.js");
    if (!response.ok) return false;

    const cart = await response.json();
    const items = /** @type {{ variant_id: number, product_id: number }[]} */ (
      cart.items ?? []
    );

    const variantSet = new Set(
      [variantId, ...blockVariantIds].filter(
        (id) => Number.isFinite(id) && id > 0,
      ),
    );
    const productSet = new Set(
      blockProductIds.filter((id) => Number.isFinite(id) && id > 0),
    );

    if (variantSet.size === 0 && productSet.size === 0) return false;

    return items.some(
      (line) =>
        variantSet.has(line.variant_id) || productSet.has(line.product_id),
    );
  } catch {
    return false;
  }
}

let mysteryCheckoutGateInstalled = false;

/**
 * Goes to Shopify checkout. Uses full navigation so it still works after `await` (e.g. closing
 * the mystery dialog), where `form.requestSubmit()` often loses user activation and does nothing.
 */
export function submitCartCheckout() {
  const routes =
    typeof Theme !== "undefined" && Theme.routes
      ? /** @type {Record<string, string>} */ (Theme.routes)
      : null;
  const configured = routes?.checkout_url;
  const url =
    typeof configured === "string" && configured.length > 0
      ? configured
      : "/checkout";

  window.location.assign(url);
}

function installMysteryCheckoutGate() {
  if (mysteryCheckoutGateInstalled) return;
  mysteryCheckoutGateInstalled = true;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(".cart__checkout-button");
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      if (!mysteryOfferPresent()) return;

      const mysteryAddon = document.querySelector(
        ".cart-drawer-addon__mystery",
      );
      const variantId =
        mysteryAddon instanceof HTMLElement
          ? Number(mysteryAddon.dataset.mysteryOfferVariantId)
          : NaN;
      const blockVariantIds =
        mysteryAddon instanceof HTMLElement
          ? (mysteryAddon.dataset.mysteryBlockVariantIds || "")
              .split(",")
              .map(Number)
              .filter((id) => Number.isFinite(id) && id > 0)
          : [];
      const blockProductIds =
        mysteryAddon instanceof HTMLElement
          ? (mysteryAddon.dataset.mysteryBlockProductIds || "")
              .split(",")
              .map(Number)
              .filter((id) => Number.isFinite(id) && id > 0)
          : [];

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      void (async () => {
        if (
          await mysteryAlreadyInCart(
            variantId,
            blockVariantIds,
            blockProductIds,
          )
        ) {
          submitCartCheckout();
          return;
        }

        getMysteryOfferDialog()?.showDialog();
      })();
    },
    true,
  );

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== "cart-form") return;

      const submitter = event.submitter;
      const isCheckout =
        submitter instanceof HTMLButtonElement &&
        submitter.name === "checkout" &&
        submitter.classList.contains("cart__checkout-button");

      if (!isCheckout) return;

      if (!mysteryCheckoutModalIsOpen()) return;

      const mysteryDialog = getMysteryOfferDialog();
      if (!mysteryDialog) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      mysteryDialog.showDialog();
    },
    true,
  );
}

/**
 * Cart Drawer Addon Component
 * @extends {Component}
 */
export class CartDrawerAddon extends Component {
  /** @type {BlazeSlider|null} */
  slider = null;
  /** @type {HTMLButtonElement|null} */
  prevButton = null;
  /** @type {HTMLButtonElement|null} */
  nextButton = null;

  constructor() {
    super();
    this.cartActions = new CartActions();
  }

  connectedCallback() {
    super.connectedCallback();
    this.initSlider();
    document.addEventListener(
      "cartDrawerAddon:render",
      this.initSlider.bind(this),
    );

    if (this.classList.contains("cart-drawer-addon__mystery")) {
      installMysteryCheckoutGate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "cartDrawerAddon:render",
      this.initSlider.bind(this),
    );
    this.destroySlider();
  }

  /**
   * Initialize the slider and navigation buttons
   */
  initSlider() {
    const sliderSelector = this.querySelector(".blaze-slider");
    const slides = this.querySelectorAll(".blaze-slide");

    if (sliderSelector) {
      // Sort slides by data-position attribute in descending order
      Array.from(slides)
        .sort(
          (
            /** @type {HTMLElement} */ { dataset: { position: a } },
            /** @type {HTMLElement} */ { dataset: { position: b } },
          ) => Number(b) - Number(a),
        )
        .forEach((item) => item.parentNode?.appendChild(item));

      // initialize the slider
      this.slider = new BlazeSlider(sliderSelector, {
        all: {
          slidesToShow: 2,
          loop: true,
        },
      });

      // Get navigation buttons
      const prevButton = this.querySelector(
        ".cart-drawer-addon__nav_button.prev-button",
      );
      const nextButton = this.querySelector(
        ".cart-drawer-addon__nav_button.next-button",
      );

      if (prevButton) {
        this.prevButton = /** @type {HTMLButtonElement} */ (prevButton);
      }

      if (nextButton) {
        this.nextButton = /** @type {HTMLButtonElement} */ (nextButton);
      }
    }
  }

  /**
   * Destroy the slider instance
   */
  destroySlider() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
      this.prevButton = null;
      this.nextButton = null;
    }
  }

  /**
   * Add to cart handler
   *
   * @param {Event} event
   */
  addToCart(event) {
    event.preventDefault();

    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest("[data-variant-id]");
    if (!(button instanceof HTMLElement)) return;

    const variantId = Number(button.dataset?.variantId);

    let properties = {};

    if (button.dataset?.properties?.length) {
      let arrayProperties = button.dataset?.properties.split(",");

      for (let property of arrayProperties) {
        let [key, value] = property.trim().split(":");
        key = key?.trim();
        value = value?.trim();

        if (key && value) {
          //@ts-ignore
          properties[key] = value;
        }
      }
    }

    if (!variantId) return;

    /**
     * @typedef {Object} CartItem
     * @property {number} id - The variant ID of the product
     * @property {number} quantity - The quantity of the product to add
     * @property {[object]} properties - Additional properties for the cart item
     *
     * @type {CartItem[]}
     */
    const items = [
      {
        id: variantId,
        quantity: 1,
        properties,
      },
    ];

    this.cartActions.addItems(items);
  }

  /**
   * Add mystery variant, then continue to checkout.
   *
   * @param {Event} event
   */
  claimMysteryAndCheckout(event) {
    event.preventDefault();

    const target = /** @type {HTMLElement} */ (event.target);
    const variantId = Number(target.dataset?.variantId);

    if (!variantId) return;

    const items = [
      {
        id: variantId,
        quantity: 1,
        properties: {},
      },
    ];

    this.cartActions.addItems(items, async () => {
      const dialog = this.querySelector("dialog-component");
      if (dialog) await dialog.closeDialog();
      submitCartCheckout();
    });
  }

  /**
   * Skip mystery offer and continue to checkout (same as Decline and the dialog X).
   *
   * @param {Event} event
   */
  async declineMysteryAndCheckout(event) {
    event.preventDefault();

    const dialog = this.querySelector("dialog-component");
    if (dialog) await dialog.closeDialog();
    submitCartCheckout();
  }

  /**
   * Go to the next slide
   */
  nextSlide() {
    if (this.slider) {
      this.slider.next();
    }
  }

  /**
   * Go to the previous slide
   */
  prevSlide() {
    if (this.slider) {
      this.slider.prev();
    }
  }
}

if (!customElements.get("cart-drawer-addon")) {
  customElements.define("cart-drawer-addon", CartDrawerAddon);
}
